import * as React from 'react';
import { useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { RootState } from '@/reducers/reducers';
import { selectUserCanPerformAction } from '@/selectors/selectors';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { OrgUser } from '@/types';
import { FetchableEntityTypeV2 } from '@/entityType';
import { useError, useIsLoading } from '@/utils/hooks/helpers';
import { antGrey } from '../styles/globalStyles';
import { vcsUserNameUpdatableId } from './EditVCSUserNamesModal';

interface VCSUserNameCellProps {
    user: OrgUser;
    orgId: string;
    vcsUserName: string;
    onEdit: () => void;
}

// Shows a user's username for one VCS provider. Editing happens in a modal covering every provider at once. Org
// admins can edit any user's username, everyone can edit their own — mirroring the check the backend enforces.
const VCSUserNameCell: React.FC<VCSUserNameCellProps> = (props: VCSUserNameCellProps) => {
    const currentUserEmail = useSelector((state: RootState) => state.user.email);
    const canEditOtherUsers = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAEditUserGroups),
    );
    const canEdit = canEditOtherUsers || props.user.email === currentUserEmail;

    const isSaving = useIsLoading(
        OrgSettingsUpdatable.UpdateVCSUserNameForOrgUser,
        vcsUserNameUpdatableId(props.user.id),
    );
    const isFetching = useIsLoading(FetchableEntityTypeV2.OrgUsersVCSUserData, props.orgId);
    const fetchError = useError(FetchableEntityTypeV2.OrgUsersVCSUserData, props.orgId);

    if (isSaving || isFetching) {
        return <LoadingOutlined style={{ color: antGrey.grey7 }} />;
    }

    // Without the usernames there's nothing to show and no safe starting point to edit from.
    if (fetchError) {
        return (
            <Tooltip title="Couldn't load VCS usernames">
                <Text type="secondary">—</Text>
            </Tooltip>
        );
    }

    if (!canEdit) {
        return <Text type={props.vcsUserName ? undefined : 'secondary'}>{props.vcsUserName || 'Not set'}</Text>;
    }

    return props.vcsUserName ?
            <Text style={{ whiteSpace: 'nowrap' }}>
                {props.vcsUserName}{' '}
                <EditOutlined style={{ color: antGrey.grey8, cursor: 'pointer' }} onClick={props.onEdit} />
            </Text>
        :   <Text
                type="secondary"
                style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                onClick={props.onEdit}
                role="button"
            >
                Not set <EditOutlined style={{ color: antGrey.grey7 }} />
            </Text>;
};

export default VCSUserNameCell;
